import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AISettings, DetectionModel, getAISettings, loadSettingsFromAPI, saveSettingsToAPI, migrateSettingsToAPI } from '../utils/aiSettings';
import { usersApi } from '../utils/api';

export const Settings: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<AISettings>(getAISettings());
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load settings from API on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        
        // Try to migrate old localStorage settings to API first
        await migrateSettingsToAPI(usersApi);
        
        // Load settings from API
        const apiSettings = await loadSettingsFromAPI(usersApi);
        setSettings(apiSettings);
      } catch (error) {
        console.error('Failed to load settings:', error);
        // Fallback to localStorage
        setSettings(getAISettings());
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      loadSettings();
    }
  }, [user]);

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaveSuccess(false);

    try {
      // Save to API (also saves to localStorage as backup)
      const success = await saveSettingsToAPI(usersApi, settings);
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
      
      if (success) {
        console.log('Settings saved successfully and synced across devices');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleModelChange = (model: DetectionModel) => {
    setSettings({
      ...settings,
      detectionModel: model
    });
  };

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setSettings({
      ...settings,
      customPrompt: e.target.value
    });
  };

  const handleMultipleItemsToggle = () => {
    setSettings({
      ...settings,
      multipleItemsPerPhoto: !settings.multipleItemsPerPhoto
    });
  };


  const handleConfidenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSettings({
      ...settings,
      detectionConfidence: parseInt(e.target.value)
    });
  };

  const handleRealtimeBoxesToggle = () => {
    setSettings({
      ...settings,
      showRealtimeBoxes: !settings.showRealtimeBoxes
    });
  };

  const handleMaxDetectionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSettings({
      ...settings,
      maxDetections: parseInt(e.target.value)
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">
          Settings
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Configure your AI detection and item creation preferences
        </p>
      </div>

      {/* AI Detection Section */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 mb-6" data-tutorial="ai-detection">
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                AI Detection
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Customize how AI analyzes and categorizes your items
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Custom Prompt */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Custom Detection Prompt
            </label>
            <textarea
              value={settings.customPrompt}
              onChange={handlePromptChange}
              rows={4}
              className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg 
                       bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100
                       focus:ring-2 focus:ring-primary-500 focus:border-transparent
                       placeholder:text-slate-400 dark:placeholder:text-slate-500"
              placeholder="e.g., Only find items related to construction and building materials. Focus on tools, equipment, and materials used in demolition and construction projects."
            />
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              This prompt will guide the AI to focus on specific types of items. Leave blank to detect all items.
            </p>
            
            {/* Example Prompts */}
            <div className="mt-3 space-y-2">
              <p className="text-xs font-medium text-slate-600 dark:text-slate-400">Example prompts:</p>
              <div className="space-y-1">
                <button
                  onClick={() => setSettings({
                    ...settings,
                    customPrompt: 'Only find items related to construction and building materials. Focus on tools, equipment, and materials.'
                  })}
                  className="block w-full text-left px-3 py-2 text-xs bg-slate-50 dark:bg-slate-700/50 rounded border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  Construction & Building Materials
                </button>
                <button
                  onClick={() => setSettings({
                    ...settings,
                    customPrompt: 'Only find furniture and home decor items. Focus on chairs, tables, sofas, and decorative pieces.'
                  })}
                  className="block w-full text-left px-3 py-2 text-xs bg-slate-50 dark:bg-slate-700/50 rounded border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  Furniture & Home Decor
                </button>
                <button
                  onClick={() => setSettings({
                    ...settings,
                    customPrompt: 'Only find electronics and technology items. Focus on computers, phones, and electronic devices.'
                  })}
                  className="block w-full text-left px-3 py-2 text-xs bg-slate-50 dark:bg-slate-700/50 rounded border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  Electronics & Technology
                </button>
              </div>
            </div>
          </div>

          {/* Multiple Items Per Photo Toggle */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700" data-tutorial="multiple-items-toggle">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Multiple Items Per Photo
                </label>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  When enabled, AI will create separate items for each distinct object detected in a photo. 
                  For example, a photo with 3 chairs will create 3 separate items.
                </p>
              </div>
              <button
                onClick={handleMultipleItemsToggle}
                className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  settings.multipleItemsPerPhoto
                    ? 'bg-primary-600'
                    : 'bg-slate-200 dark:bg-slate-700'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    settings.multipleItemsPerPhoto ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            
            {settings.multipleItemsPerPhoto && (
              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    When this is enabled, each photo may generate multiple items. The AI will attempt to identify and create separate listings for each distinct object in the image.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Detection Model */}
          <div className={`pt-6 border-t border-slate-200 dark:border-slate-700 ${!settings.multipleItemsPerPhoto ? 'opacity-50 pointer-events-none' : ''}`} data-tutorial="detection-model">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Detection Model
            </label>
            {!settings.multipleItemsPerPhoto && (
              <div className="mb-3 p-2 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg">
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  <strong>Note:</strong> Object detection is disabled because "Multiple Items Per Photo" is turned off. Enable it above to configure detection settings.
                </p>
              </div>
            )}
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              💡 <strong>Tip:</strong> Select "Manual Only" for instant camera (no model loading). Or test different models to compare speed vs accuracy.
            </p>
            
            <div className="space-y-3">
              {/* Lite Model */}
              <button
                onClick={() => handleModelChange('lite')}
                disabled={!settings.multipleItemsPerPhoto}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  settings.detectionModel === 'lite'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        ⚡ Lite Model (Fastest)
                      </span>
                      <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                        Recommended
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      MobileNet V2 Lite. Fastest (~15ms), good accuracy. ~2.5MB model. Best for real-time detection.
                    </p>
                  </div>
                  {settings.detectionModel === 'lite' && (
                    <svg className="w-5 h-5 text-primary-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </button>

              {/* MobileNet Model */}
              <button
                onClick={() => handleModelChange('mobilenet')}
                disabled={!settings.multipleItemsPerPhoto}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  settings.detectionModel === 'mobilenet'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        🎯 MobileNet Model (More Accurate)
                      </span>
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                        Better Quality
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      MobileNet V2. Slower (~25ms), better accuracy. ~8MB model. Best for quality over speed.
                    </p>
                  </div>
                  {settings.detectionModel === 'mobilenet' && (
                    <svg className="w-5 h-5 text-primary-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </button>

              {/* None/Manual */}
              <button
                onClick={() => handleModelChange('none')}
                disabled={!settings.multipleItemsPerPhoto}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  settings.detectionModel === 'none'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        ✋ Manual Only
                      </span>
                      <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded">
                        Instant Load
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      No automatic detection. Camera opens instantly (no model loading). Process photos manually from Photos page.
                    </p>
                  </div>
                  {settings.detectionModel === 'none' && (
                    <svg className="w-5 h-5 text-primary-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* Detection Settings (only show if not manual and multiple items enabled) */}
          {settings.multipleItemsPerPhoto && settings.detectionModel !== 'none' && (
            <>
              {/* Confidence Threshold */}
              <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Detection Confidence: {settings.detectionConfidence}%
                </label>
                <input
                  type="range"
                  min="30"
                  max="90"
                  value={settings.detectionConfidence}
                  onChange={handleConfidenceChange}
                  className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
                />
                <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mt-1">
                  <span>30% (More items, less accurate)</span>
                  <span>90% (Fewer items, more accurate)</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  Only detect objects with confidence above this threshold. Lower = more detections but more false positives.
                </p>
              </div>

              {/* Max Detections */}
              <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Maximum Detections: {settings.maxDetections} objects
                </label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="10"
                  value={settings.maxDetections}
                  onChange={handleMaxDetectionsChange}
                  className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
                />
                <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mt-1">
                  <span>10 objects (Faster)</span>
                  <span>100 objects (More thorough)</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  Maximum number of objects to detect in a single photo. Higher values may be slower but find more items.
                </p>
              </div>

              {/* Real-time Boxes Toggle */}
              <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Show Real-Time Bounding Boxes
                    </label>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Display detection boxes on camera feed while taking photos. May affect performance on slower devices.
                    </p>
                  </div>
                  <button
                    onClick={handleRealtimeBoxesToggle}
                    className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                      settings.showRealtimeBoxes
                        ? 'bg-primary-600'
                        : 'bg-slate-200 dark:bg-slate-700'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        settings.showRealtimeBoxes ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-between">
        <div>
          {saveSuccess && (
            <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm font-medium">Settings saved successfully!</span>
            </div>
          )}
        </div>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="btn-primary flex items-center space-x-2"
          data-tutorial="save-settings"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Save Settings</span>
            </>
          )}
        </button>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg">
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-slate-600 dark:text-slate-400">
            <p className="font-medium mb-1">How AI Detection Works</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>AI analyzes photos to identify items and estimate their value</li>
              <li>Custom prompts help narrow down detection to specific categories</li>
              <li>Multiple items mode creates separate listings for each object found</li>
              <li>Settings apply to all future photo uploads and camera captures</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

